import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Ban, CheckCircle2, Clock, Download, FileCheck2, FileImage, Handshake, Inbox, Loader2,
    Mail, PackageCheck, Plus, Printer, Save, Store, Trash2,
} from "lucide-react";
import BackOffice from "@/components/account/BackOffice";
import { Btn, PageHead, Section, TONES, inputCls } from "@/components/account/Shell";
import { Cell, Dash, Filters, PillRow, Sheet, Tag as STag, Two, Txt } from "@/components/account/Sheet";
import api, { formatApiError } from "@/lib/api";

const TONE = TONES.orders;
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const cell = "rounded border border-black/15 bg-white px-2 py-1 text-sm text-zinc-900 outline-none focus:border-zinc-900";
import { when as day } from "@/lib/when";

const FOLDERS = [
    { key: "all", label: "All", icon: Inbox },
    { key: "pending_payment", label: "Awaiting payment", icon: Clock },
    { key: "ready", label: "Ready for pickup", icon: PackageCheck },
    { key: "completed", label: "Picked up", icon: CheckCircle2 },
    { key: "cancelled", label: "Cancelled", icon: Ban },
    { key: "proofs", label: "Proofs waiting", icon: FileCheck2 },
];
const PROOF_WAITING = ["sent", "pending", "changes_requested"];

const STATUS_CLS = {
    pending_payment: "border-[#b54708] bg-[#fffaeb] text-[#b54708]",
    paid: "border-[#067647] bg-[#ecfdf3] text-[#067647]",
    in_production: "border-[#1a56db] bg-[#eff4ff] text-[#1a56db]",
    ready: "border-[#067647] bg-[#ecfdf3] text-[#067647]",
    completed: "border-[#d0d0d0] bg-[#f5f5f5] text-[#1a1a1a]",
    cancelled: "border-[#b42318] bg-[#fef3f2] text-[#b42318]",
};

const Tag = ({ children, cls = "border-[#d0d0d0] bg-[#f5f5f5] text-[#1a1a1a]", testid }) => (
    <span data-testid={testid}
        className={`inline-flex shrink-0 items-center whitespace-nowrap border px-1.5 py-px text-[11px] font-semibold uppercase ${cls}`}>
        {children}
    </span>
);

function LineRow({ item, onSave, onDelete, busy, readOnly }) {
    const [form, setForm] = useState({ name: item.product_name || "", qty: item.quantity ?? 1, price: item.unit_price ?? 0 });
    useEffect(() => setForm({ name: item.product_name || "", qty: item.quantity ?? 1, price: item.unit_price ?? 0 }),
        [item.uid, item.product_name, item.quantity, item.unit_price]);
    const dirty = form.name !== item.product_name || String(form.qty) !== String(item.quantity)
        || String(form.price) !== String(item.unit_price);

    return (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-black/10 px-2.5 py-1.5 first:border-t-0"
            data-testid={`vendor-item-${item.uid}`}>
            {item.proof?.url && (
                <img src={item.proof.url} alt="" className="h-9 w-9 shrink-0 rounded border border-black/10 object-cover" />
            )}
            {readOnly ? (
                <span className="min-w-[160px] flex-1 text-sm font-semibold text-zinc-900">{item.product_name}</span>
            ) : (
                <input className={`${cell} min-w-[150px] flex-1 font-semibold`} value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    data-testid={`vendor-item-name-${item.uid}`} />
            )}
            <span className="hidden max-w-[220px] truncate text-xs text-zinc-700 lg:block">{item.summary}</span>
            {readOnly ? (
                <span className="font-mono text-xs text-zinc-800">×{item.quantity} @ {money(item.unit_price)}</span>
            ) : (
                <>
                    <input type="number" min="1" className={`${cell} w-14`} value={form.qty}
                        onChange={(e) => setForm((f) => ({ ...f, qty: e.target.value }))}
                        data-testid={`vendor-item-qty-${item.uid}`} />
                    <input type="number" min="0" step="0.01" className={`${cell} w-20`} value={form.price}
                        onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                        data-testid={`vendor-item-price-${item.uid}`} />
                </>
            )}
            <span className="w-20 text-right font-mono text-sm font-bold tabular-nums text-zinc-900"
                data-testid={`vendor-item-total-${item.uid}`}>{money(item.line_total)}</span>
            {(item.artwork || []).map((a, i) => (
                <a key={i} href={a.url} target="_blank" rel="noreferrer" data-testid={`vendor-art-${item.uid}-${i}`}
                    className="inline-flex items-center gap-1 font-mono text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-800 hover:text-zinc-900 hover:underline">
                    <Download size={10} /> {a.filename || `art ${i + 1}`}
                </a>
            ))}
            {item.proof?.url && (
                <a href={item.proof.url} target="_blank" rel="noreferrer" data-testid={`vendor-proof-${item.uid}`}
                    className="inline-flex items-center gap-1 font-mono text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-800 hover:underline">
                    <FileImage size={10} /> proof
                </a>
            )}
            {item.consignment && (
                <Tag testid={`vendor-consign-${item.uid}`} cls="border-[#1a56db] bg-[#eff4ff] text-[#1a56db]">
                    consignment · {money(item.consignment.seller_share)} to {item.consignment.seller_name}
                </Tag>
            )}
            {!readOnly && (
                <>
                    <Btn onClick={() => onSave(item.uid, {
                        product_name: form.name, quantity: Number(form.qty), unit_price: Number(form.price),
                    })} disabled={!dirty || busy} testid={`vendor-item-save-${item.uid}`} tone={dirty ? "solid" : "line"}>
                        <Save size={10} /> Save
                    </Btn>
                    <button onClick={() => onDelete(item.uid)} disabled={busy} data-testid={`vendor-item-delete-${item.uid}`}
                        className="text-zinc-600 transition-colors hover:text-red-600"><Trash2 size={13} /></button>
                </>
            )}
        </div>
    );
}

function OrderWindow({ order, labels, statuses, reload, onBack }) {
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState("");
    const [notify, setNotify] = useState(false);
    const [line, setLine] = useState({ product_name: "", quantity: 1, unit_price: "" });

    const run = async (fn) => {
        setBusy(true);
        try { const { data } = await fn(); reload(data.order); return data; }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const consignedOnly = order.consignment_only;

    return (
        <div className="overflow-hidden rounded-lg border border-black/10 bg-white" data-testid="vendor-order-detail">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-black/10 bg-zinc-100 px-3 py-2">
                <button onClick={onBack} data-testid="vendor-detail-back"
                    className="inline-flex items-center gap-1 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-700 hover:text-zinc-900">
                    <ArrowLeft size={12} /> close
                </button>
                <span className="font-mono text-[11px] font-black tracking-[0.14em] text-zinc-900">{order.order_number}</span>
                <Tag cls={STATUS_CLS[order.status] || "border-[#d0d0d0] bg-[#f5f5f5] text-[#1a1a1a]"} testid="vendor-detail-status">
                    {labels?.[order.status] || order.status}
                </Tag>
                {order.paid ? <Tag testid="vendor-paid-flag" cls="border-[#067647] bg-[#ecfdf3] text-[#067647]">paid{order.paid_by === "card" ? " · card" : ""}</Tag>
                    : <Tag cls="border-[#b54708] bg-[#fffaeb] text-[#b54708]">unpaid</Tag>}
                {consignedOnly && <Tag testid="vendor-consignment-only" cls="border-[#1a56db] bg-[#eff4ff] text-[#1a56db]">consignment sale</Tag>}
                <span className="ml-auto font-mono text-[11px] uppercase tracking-[0.12em] text-zinc-700">
                    {new Date(order.created_at).toLocaleString()}
                </span>
                <span className="font-mono text-lg font-black tabular-nums text-zinc-900" data-testid="vendor-my-total">
                    {money(order.my_total)}
                </span>
            </div>

            <div className="grid gap-x-5 gap-y-2 px-3 py-2.5 text-sm sm:grid-cols-2">
                <p className="text-zinc-900">
                    <span className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-700">Customer · </span>
                    <span className="font-semibold">{order.contact?.name || "Walk-in"}</span>
                    {order.contact?.phone ? ` · ${order.contact.phone}` : ""}
                    {order.contact?.email ? ` · ${order.contact.email}` : ""}
                </p>
                <p className="text-zinc-900">
                    <span className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-700">Order total · </span>
                    {money(order.order_total)}
                    {order.member_tier_name ? ` · ${order.member_tier_name} member` : ""}
                    {order.consignment_owed ? ` · ${money(order.consignment_owed)} owed to the shop that sold it` : ""}
                </p>
                {order.notes && (
                    <p className="text-zinc-900 sm:col-span-2" data-testid="vendor-order-notes">
                        <span className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-700">Customer notes · </span>
                        {order.notes}
                    </p>
                )}
                {!!order.shared_with?.length && (
                    <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-zinc-700 sm:col-span-2"
                        data-testid="vendor-shared-with">
                        shared order · also {order.shared_with.map((s) => s.name).join(", ")}
                    </p>
                )}
            </div>

            {!consignedOnly && (
                <div className="flex flex-wrap items-center gap-1.5 border-t border-black/10 px-3 py-2" data-testid="vendor-status-buttons">
                    <span className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-700">Move this job</span>
                    {statuses.map((s) => (
                        <button key={s} onClick={() => run(() => api.patch(`/vendor/orders/${order.id}/status`, { status: s }))}
                            disabled={busy} data-testid={`vendor-status-${s}`}
                            className={`rounded-full border px-2.5 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.12em] disabled:opacity-50 ${order.status === s
                                ? "border-[#1a1a1a] bg-[#1a1a1a] text-white bo-inv" : "border-[#d0d0d0] bg-white text-[#1a1a1a] hover:border-[#1a1a1a]"}`}>
                            {labels?.[s] || s}
                        </button>
                    ))}
                    <Link to={`/account/orders/${order.id}/ticket`} target="_blank" rel="noreferrer"
                        data-testid="vendor-work-ticket" className="ml-auto">
                        <Btn testid="vendor-work-ticket-btn"><Printer size={10} /> work ticket</Btn>
                    </Link>
                </div>
            )}

            {!!order.items.length && (
                <div data-testid="vendor-items">
                    <p className="border-y border-black/10 bg-zinc-50 px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-800">
                        {order.items.length} line{order.items.length === 1 ? "" : "s"} you make
                    </p>
                    {order.items.map((it) => (
                        <LineRow key={it.uid} item={it} busy={busy}
                            onSave={(uid, patch) => run(() => api.put(`/vendor/orders/${order.id}/items/${uid}`, patch))
                                .then((d) => d && toast.success("Line updated"))}
                            onDelete={(uid) => run(() => api.delete(`/vendor/orders/${order.id}/items/${uid}`))} />
                    ))}
                </div>
            )}

            {!!(order.consigned_items || []).length && (
                <div data-testid="vendor-consigned-items">
                    <p className="flex flex-wrap items-center gap-2 border-y border-black/10 bg-zinc-50 px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-800">
                        <Handshake size={11} /> {order.consigned_items.length} line{order.consigned_items.length === 1 ? "" : "s"} on
                        consignment — 13th Ave makes these · {money(order.consignment_earned)} yours
                    </p>
                    {order.consigned_items.map((it) => (
                        <LineRow key={it.uid} item={it} busy={busy} readOnly onSave={() => { }} onDelete={() => { }} />
                    ))}
                </div>
            )}

            {!consignedOnly && (
                <div className="flex flex-wrap items-center gap-1.5 border-t border-black/10 px-2.5 py-2" data-testid="vendor-add-line">
                    <span className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-700">Add a line</span>
                    <input className={`${cell} min-w-[160px] flex-1`} placeholder="Rush fee, extra print, screen charge…"
                        value={line.product_name} onChange={(e) => setLine({ ...line, product_name: e.target.value })}
                        data-testid="vendor-line-name" />
                    <input type="number" min="1" className={`${cell} w-14`} value={line.quantity}
                        onChange={(e) => setLine({ ...line, quantity: e.target.value })} data-testid="vendor-line-qty" />
                    <input type="number" min="0" step="0.01" className={`${cell} w-20`} placeholder="Each $"
                        value={line.unit_price} onChange={(e) => setLine({ ...line, unit_price: e.target.value })}
                        data-testid="vendor-line-price" />
                    <Btn tone="solid" disabled={busy} testid="vendor-line-add"
                        onClick={() => {
                            if (!line.product_name.trim()) { toast.error("Name the line first"); return; }
                            run(() => api.post(`/vendor/orders/${order.id}/items`, {
                                ...line, quantity: Number(line.quantity) || 1, unit_price: Number(line.unit_price) || 0,
                            })).then(() => setLine({ product_name: "", quantity: 1, unit_price: "" }));
                        }}>
                        <Plus size={10} /> Add
                    </Btn>
                </div>
            )}

            <div className="border-t border-black/10 px-3 py-2" data-testid="vendor-comments">
                <p className="font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-800">Shop notes</p>
                <div className="mt-1.5 space-y-1">
                    {(order.comments || []).map((c) => (
                        <p key={c.id} className="text-sm text-zinc-900" data-testid={`vendor-comment-${c.id}`}>
                            {c.body}
                            <span className="ml-2 font-mono text-[11px] uppercase tracking-[0.1em] text-zinc-700">
                                {c.author} · {new Date(c.created_at).toLocaleDateString()}
                                {c.sent_to_customer ? " · emailed" : ""}
                            </span>
                        </p>
                    ))}
                    {!(order.comments || []).length && <p className="text-sm text-zinc-700">No notes on this job yet.</p>}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                    <textarea className={`${inputCls} min-w-[220px] flex-1`} rows={2} value={note}
                        placeholder="What's happening with this job…" onChange={(e) => setNote(e.target.value)}
                        data-testid="vendor-comment-input" />
                    <label className="inline-flex items-center gap-1.5 text-xs text-zinc-900">
                        <input type="checkbox" className="h-4 w-4 accent-zinc-900" checked={notify}
                            onChange={(e) => setNotify(e.target.checked)} data-testid="vendor-comment-notify" />
                        email the customer
                    </label>
                    <Btn tone="solid" disabled={busy || !note.trim()} testid="vendor-comment-save"
                        onClick={() => run(() => api.post(`/vendor/orders/${order.id}/comments`, { body: note, notify_customer: notify }))
                            .then((d) => { if (d) { setNote(""); toast.success(notify ? "Sent to the customer" : "Note saved"); } })}>
                        Post note
                    </Btn>
                </div>
            </div>
        </div>
    );
}

const COLS = [
    { key: "num", label: "Order #", sub: "click the row to open it", w: "110px" },
    { key: "when", label: "Placed", sub: "date · how long ago", w: "150px" },
    { key: "customer", label: "Customer", sub: "name · phone", w: "minmax(170px,1.3fr)" },
    { key: "lines", label: "Lines", sub: "how many · the first one", w: "minmax(200px,1.6fr)" },
    { key: "proof", label: "Proof", sub: "where the proof stands", w: "120px" },
    { key: "paid", label: "Paid", w: "100px" },
    { key: "status", label: "Status", w: "140px" },
    { key: "total", label: "Yours", sub: "$ your lines on this order", w: "100px" },
];
const PROOF_LABEL = { sent: "sent · waiting", pending: "waiting", changes_requested: "changes asked", approved: "approved" };

export default function VendorOrders() {
    const [params, setParams] = useSearchParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [selected, setSelected] = useState(null);
    const [digesting, setDigesting] = useState(false);
    const folder = params.get("status") || "all";

    const load = useCallback(() => {
        api.get("/vendor/orders").then(({ data }) => {
            setData(data);
            setSelected((prev) => (prev ? data.orders.find((o) => o.id === prev.id) || prev : null));
        }).catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Could not load your orders"));
    }, []);
    useEffect(load, [load]);

    const counts = useMemo(() => {
        const rows = data?.orders || [];
        return {
            all: rows.length,
            proofs: rows.filter((o) => PROOF_WAITING.includes(o.proof_status)).length,
            ...FOLDERS.reduce((acc, f) => {
                if (f.key !== "all" && f.key !== "proofs") acc[f.key] = data?.counts?.[f.key] || 0;
                return acc;
            }, {}),
        };
    }, [data]);

    const setFolder = (key) => { setParams(key === "all" ? {} : { status: key }); setSelected(null); };

    if (err) return (
        <BackOffice><div className="mx-auto max-w-2xl py-16 text-center" data-testid="vendor-orders-locked">
            <Store size={30} className="mx-auto text-zinc-500" />
            <h1 className="mt-3 font-display text-2xl font-black text-zinc-900">Orders live with your space.</h1>
            <p className="mt-2 text-sm text-zinc-700">{err} Open a Starter, Storefront or Pro Shop and this becomes your job board.</p>
            <Link to="/account/sell" className="mt-4 inline-block">
                <Btn tone="solid" testid="vendor-orders-open-space">See the spaces</Btn>
            </Link>
        </div></BackOffice>
    );
    if (!data) return <BackOffice><div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="animate-spin text-zinc-500" /></div></BackOffice>;

    const shown = folder === "all" ? data.orders
        : folder === "proofs" ? data.orders.filter((o) => PROOF_WAITING.includes(o.proof_status))
            : data.orders.filter((o) => o.status === folder);
    const open = selected ? data.orders.find((o) => o.id === selected.id) || selected : null;
    const folderLabel = FOLDERS.find((f) => f.key === folder)?.label;

    const sendDigest = async () => {
        setDigesting(true);
        try {
            const { data: res } = await api.post("/vendor/orders/digest");
            toast.success(`Digest emailed to ${res.to} — ${res.orders} open job(s)`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setDigesting(false); }
    };

    return (
        <BackOffice>
            <PageHead kicker="Orders" tone={TONE} title="Order inbox" testid="vendor-orders-title"
                sub={`Every job that lands in ${data.store?.name || "your shop"} — emailed the second it does, plus a 7am summary of everything still open. Click a row to open the order right under it.`}
                right={
                    <div className="text-right">
                        <p className="font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-700">
                            Booked ({data.orders.length} orders)
                        </p>
                        <p className="font-mono text-2xl font-black tabular-nums text-zinc-900" data-testid="vendor-revenue">
                            {money(data.revenue)}
                        </p>
                        <div className="mt-1.5 flex justify-end gap-1.5">
                            <Link to="/account/inbox" data-testid="vendor-folder-messages"><Btn><Mail size={11} /> shop inbox</Btn></Link>
                            <Btn onClick={sendDigest} disabled={digesting} testid="vendor-digest-now">
                                {digesting ? <Loader2 className="animate-spin" size={11} /> : <Mail size={11} />} email today's digest
                            </Btn>
                        </div>
                    </div>
                } />

            <Filters testid="vendor-status-filter">
                <PillRow label="Show" testid="vendor-folder" value={folder} onPick={setFolder}
                    choices={FOLDERS.map((f) => ({ key: f.key, label: f.label, count: counts[f.key] || 0 }))} />
            </Filters>

            <div className="mt-3">
                <Section title={`${folderLabel} · ${shown.length}`} pad={false} testid="vendor-order-list"
                    note={open ? `${open.order_number} is open below its row` : "newest first"}>
                    <Sheet cols={COLS} rows={shown} rowKey={(o) => o.id} testid="vendor-orders-sheet" minWidth={1040}
                        rowTestid={(o) => `vendor-order-${o.order_number}`}
                        onRowClick={(o) => setSelected(open?.id === o.id ? null : o)} active={(o) => open?.id === o.id}
                        expand={(o) => (
                            <OrderWindow order={o} labels={data.labels} statuses={data.statuses}
                                onBack={() => setSelected(null)}
                                reload={(u) => {
                                    setSelected(u);
                                    setData((d) => (d ? { ...d, orders: d.orders.map((x) => (x.id === u.id ? { ...x, ...u } : x)) } : d));
                                }} />
                        )}
                        empty={data.orders.length ? `Nothing sitting in ${folderLabel} right now.` : "No orders yet. The second someone checks out with your work it shows up here."}
                        cells={(o) => {
                            const n = o.items.length + (o.consigned_items?.length || 0);
                            return {
                                num: <Cell><Txt bold mono>{o.order_number}</Txt></Cell>,
                                when: <Cell><Txt sub>{day(o.created_at)}</Txt></Cell>,
                                customer: <Cell><Two a={o.contact?.name || "Walk-in"} b={o.contact?.phone || o.contact?.email} /></Cell>,
                                lines: <Cell><Two a={`${n} line${n === 1 ? "" : "s"}${o.consignment_only ? " · consignment" : ""}`} b={(o.items[0] || o.consigned_items?.[0])?.product_name || "—"} /></Cell>,
                                proof: <Cell>{o.proof_status ? <STag status={PROOF_WAITING.includes(o.proof_status) ? "pending" : "active"}>{PROOF_LABEL[o.proof_status] || o.proof_status}</STag> : <Dash why="No proof on this order" />}</Cell>,
                                paid: <Cell>{o.paid ? <STag status="paid">paid{o.paid_by === "card" ? " · card" : ""}</STag> : <STag status="pending_payment">unpaid</STag>}</Cell>,
                                status: <Cell><Tag cls={STATUS_CLS[o.status] || "border-[#d0d0d0] bg-[#f5f5f5] text-[#1a1a1a]"}>{data.labels?.[o.status] || o.status}</Tag></Cell>,
                                total: <Cell><Txt bold mono right>{money(o.my_total)}</Txt></Cell>,
                            };
                        }} />
                    {!shown.length && data.orders.length > 0 && (
                        <div className="border-t border-[#d0d0d0] px-2.5 py-2 text-center" data-testid="vendor-orders-none-in-filter">
                            <button onClick={() => setFolder("all")} data-testid="vendor-filter-clear"
                                className="font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-900 hover:underline">
                                show all {data.orders.length} jobs
                            </button>
                        </div>
                    )}
                </Section>
            </div>
        </BackOffice>
    );
}
